import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnInit,
  inject,
  input,
  output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import {
  ClientExternalProvider,
  ClientService,
  ToastMessageService,
} from '@fitness-force/api';
import { ErrorMsgComponent } from '@fitness-force/errors';
import {
  ExternalProviderCategory,
  ExternalProviderType,
} from '@fitness-force/enum';
import type { TENANTSINTERFACE } from '@fitness-force/models';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { AvatarModule } from 'primeng/avatar';
import { BlockUIModule } from 'primeng/blockui';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { RippleModule } from 'primeng/ripple';
import { StyleClassModule } from 'primeng/styleclass';
import { BehaviorSubject } from 'rxjs';

@Component({
  selector: 'assign-fitpass',
  templateUrl: './assign-fitpass.component.html',
  styleUrls: ['./assign-fitpass.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    DialogModule,
    AvatarModule,
    ButtonModule,
    RippleModule,
    BlockUIModule,
    StyleClassModule,
    InputTextModule,
    ErrorMsgComponent,
    TranslateModule,
    AsyncPipe,
  ],
})
export class AssignFitpassComponent implements OnInit {
  private readonly fb = inject(UntypedFormBuilder);
  private readonly translate = inject(TranslateService);
  private readonly clientService = inject(ClientService);
  private readonly toastService = inject(ToastMessageService);
  private readonly store = inject(Store);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  readonly assignFitpassModal = input<boolean | null>(false);
  readonly clientData$ = input<BehaviorSubject<any>>(
    new BehaviorSubject<any>(null),
  );
  readonly clientId = input<number>(0);
  readonly closeAssignFitpassModalDialog = output<any>();

  assignFitpassFormGroup: UntypedFormGroup = new UntypedFormGroup({});
  validAlphaNumericPattern = '^[a-zA-Z0-9]*$';
  allClientData: any = null;
  tenantId = 0;
  btnLoading = false;
  fitpassMapping: ClientExternalProvider | null = null;

  ngOnInit(): void {
    this.initForm();
    this.componentInit();
  }

  private initForm(): void {
    this.assignFitpassFormGroup = this.fb.group({
      fitpassId: [
        '',
        [
          Validators.required,
          Validators.pattern(this.validAlphaNumericPattern),
        ],
      ],
    });

    this.assignFitpassFormGroup
      .get('fitpassId')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.changeDetectorRef.markForCheck());
  }

  componentInit(): void {
    this.store
      .select((state: any) => state.tenants)
      .subscribe((tenant: TENANTSINTERFACE) => {
        if (tenant?.selected_tenant) {
          this.tenantId = tenant.selected_tenant.id;
        }
      });

    const clientDataSubject = this.clientData$();
    if (clientDataSubject) {
      clientDataSubject.subscribe((res: any) => {
        this.allClientData = res;
      });
    }

    const clientId = this.clientId();
    if (clientId > 0) {
      this.loadFitpassMapping(clientId);
      this.clientService.getClientById(clientId).subscribe({
        next: (res: any) => {
          this.allClientData = res;
          this.clientData$().next(res);
        },
        error: () => {
          // Handle error silently
        },
      });
    }
  }

  loadFitpassMapping(clientId: number): void {
    if (!clientId) {
      return;
    }

    this.clientService.listClientExternalProviders(clientId).subscribe({
      next: (response) => {
        this.fitpassMapping =
          response.items?.find((item) => this.isFitpassMapping(item)) ?? null;

        this.assignFitpassFormGroup.patchValue({
          fitpassId: this.fitpassMapping?.provider_id ?? '',
        });
        this.changeDetectorRef.markForCheck();
      },
      error: () => {
        this.assignFitpassFormGroup.patchValue({ fitpassId: '' });
        this.changeDetectorRef.markForCheck();
      },
    });
  }

  private isFitpassMapping(item: ClientExternalProvider): boolean {
    const category = item.category;
    const provider = item.provider;

    return (
      category === ExternalProviderCategory.FitPass ||
      category === 'FitPass' ||
      provider === ExternalProviderType.FitPassId ||
      provider === 'FitPassId'
    );
  }

  get fitpassId(): UntypedFormControl {
    return this.assignFitpassFormGroup.controls
      .fitpassId as UntypedFormControl;
  }

  closeDialogEvent(): void {
    this.closeAssignFitpassModalDialog.emit(false);
  }

  deleteFitpass(): void {
    if (!this.allClientData?.id || !this.fitpassMapping?.id) {
      this.toastService.showErrorMessage('FitPass mapping is not available');
      return;
    }

    this.btnLoading = true;
    this.clientService
      .deleteClientExternalProvider(
        this.tenantId,
        this.allClientData.id,
        this.fitpassMapping.id,
      )
      .subscribe({
        next: async () => {
          this.btnLoading = false;
          this.translate
            .get('CLIENT_PROFILE.FITPASS.DELETE_SUCCESS_MSG')
            .subscribe((translation) => {
              this.toastService.showSuccess(translation);
            });
          await this.timeout(1000);
          this.closeAssignFitpassModalDialog.emit({ action: 'updated' });
        },
        error: (error) => {
          this.btnLoading = false;
          this.handleError(error);
        },
      });
  }

  postAssignFitpass(): void {
    if (!this.allClientData?.id) {
      this.toastService.showErrorMessage('Client data is not available');
      return;
    }

    if (!this.assignFitpassFormGroup.valid) {
      Object.keys(this.assignFitpassFormGroup.controls).forEach((field) => {
        const control = this.assignFitpassFormGroup.get(field);
        control?.markAsDirty({ onlySelf: true });
      });
      return;
    }

    const currentValue = this.fitpassId.value?.trim() || '';
    const currentFitpassId = this.fitpassMapping?.provider_id || '';
    const isFitpassChanged = currentValue !== currentFitpassId;

    if (!isFitpassChanged) {
      this.closeAssignFitpassModalDialog.emit(false);
      return;
    }

    this.btnLoading = true;
    const request = {
      category: ExternalProviderCategory.FitPass,
      provider: ExternalProviderType.FitPassId,
      provider_id: currentValue,
      is_internal: false,
    };

    const save$ = this.fitpassMapping?.id
      ? this.clientService.updateClientExternalProvider(
          this.tenantId,
          this.allClientData.id,
          this.fitpassMapping.id,
          request,
        )
      : this.clientService.createClientExternalProvider(
          this.tenantId,
          this.allClientData.id,
          request,
        );

    save$.subscribe({
      next: async () => {
        this.btnLoading = false;
        this.translate
          .get('CLIENT_PROFILE.FITPASS.SUCCESS_MSG')
          .subscribe((translation) => {
            this.toastService.showSuccess(translation);
          });
        await this.timeout(1000);
        this.closeAssignFitpassModalDialog.emit({ action: 'updated' });
      },
      error: (error) => {
        this.btnLoading = false;
        this.handleError(error);
      },
    });
  }

  get canSubmitForm(): boolean {
    const control = this.assignFitpassFormGroup.get('fitpassId');
    const currentValue = control?.value?.trim() || '';
    return currentValue.length >= 1 && (control?.valid ?? false);
  }

  handleError(error: any): void {
    if (error?.originalError?.error?.length > 0) {
      error.originalError.error.forEach((e: any) => {
        this.toastService.showErrorMessage(e.errorMessage);
      });
    } else if (error?.originalError?.error?.errorMessage) {
      this.toastService.showErrorMessage(
        error.originalError.error.errorMessage,
      );
    } else {
      this.toastService.showErrorMessage('An error occurred');
    }
    this.changeDetectorRef.detectChanges();
  }

  timeout(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}
